import React, { useState } from "react";
import hosys from "@/api/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const TYPES = [
  { value: "quadre_general",   label: "Quadre general" },
  { value: "quadre_secundari", label: "Quadre secundari" },
  { value: "grup_electrogen",  label: "Grup electrogen" },
  { value: "ups",              label: "SAI/UPS" },
  { value: "xarxa_illuminacio",label: "Xarxa il·luminació" },
  { value: "xarxa_endolls",    label: "Xarxa endolls" },
  { value: "climatitzacio",    label: "Climatització" },
  { value: "cuina_industrial", label: "Cuina industrial" },
  { value: "altres",           label: "Altres" },
];

const STATUSES = [
  { value: "ok",                  label: "Conforme" },
  { value: "pending_inspection",  label: "Pendent inspecció" },
  { value: "expired",             label: "Caducat" },
  { value: "deficiency_minor",    label: "Deficiència lleu" },
  { value: "deficiency_major",    label: "Deficiència greu" },
  { value: "inactive",            label: "Inactiu" },
];

export default function InstallationForm({ installation, venueId, onSave, onClose }) {
  const [form, setForm] = useState({
    venue_id: venueId,
    name: "",
    installation_type: "quadre_general",
    location: "",
    power_kw: "",
    voltage_v: 230,
    installation_date: "",
    last_inspection_date: "",
    next_inspection_date: "",
    inspection_frequency_years: 5,
    rebt_itc: "ITC-BT-28",
    installer_company: "",
    installer_license: "",
    status: "ok",
    notes: "",
    active: true,
    ...installation,
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const data = { ...form, power_kw: form.power_kw ? Number(form.power_kw) : undefined, voltage_v: form.voltage_v ? Number(form.voltage_v) : undefined, inspection_frequency_years: Number(form.inspection_frequency_years) };
    if (installation?.id) {
      await hosys.entities.ElectricalInstallation.update(installation.id, data);
    } else {
      await hosys.entities.ElectricalInstallation.create(data);
    }
    setSaving(false);
    onSave();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{installation?.id ? "Editar instal·lació" : "Nova instal·lació"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label>Nom / Descripció *</Label>
              <Input value={form.name} onChange={e => set("name", e.target.value)} required placeholder="Ex: Quadre principal sala" />
            </div>
            <div>
              <Label>Tipus *</Label>
              <Select value={form.installation_type} onValueChange={v => set("installation_type", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Estat</Label>
              <Select value={form.status} onValueChange={v => set("status", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Ubicació</Label>
              <Input value={form.location} onChange={e => set("location", e.target.value)} placeholder="Ex: Soterrani, cuina..." />
            </div>
            <div>
              <Label>Potència (kW)</Label>
              <Input type="number" step="0.1" value={form.power_kw} onChange={e => set("power_kw", e.target.value)} placeholder="Ex: 15.5" />
            </div>
            <div>
              <Label>Tensió (V)</Label>
              <Input type="number" value={form.voltage_v} onChange={e => set("voltage_v", e.target.value)} placeholder="230 / 400" />
            </div>
            <div>
              <Label>ITC-BT aplicable</Label>
              <Input value={form.rebt_itc} onChange={e => set("rebt_itc", e.target.value)} placeholder="ITC-BT-28" />
            </div>
            <div>
              <Label>Data instal·lació / última reforma</Label>
              <Input type="date" value={form.installation_date} onChange={e => set("installation_date", e.target.value)} />
            </div>
            <div>
              <Label>Última inspecció</Label>
              <Input type="date" value={form.last_inspection_date} onChange={e => set("last_inspection_date", e.target.value)} />
            </div>
            <div>
              <Label>Pròxima inspecció</Label>
              <Input type="date" value={form.next_inspection_date} onChange={e => set("next_inspection_date", e.target.value)} />
            </div>
            <div>
              <Label>Periodicitat inspecció (anys)</Label>
              <Input type="number" value={form.inspection_frequency_years} onChange={e => set("inspection_frequency_years", e.target.value)} />
            </div>
            <div>
              <Label>Empresa instal·ladora</Label>
              <Input value={form.installer_company} onChange={e => set("installer_company", e.target.value)} placeholder="Nom empresa" />
            </div>
            <div>
              <Label>Llicència instal·lador</Label>
              <Input value={form.installer_license} onChange={e => set("installer_license", e.target.value)} placeholder="Núm. llicència" />
            </div>
            <div className="sm:col-span-2">
              <Label>Notes tècniques</Label>
              <Textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel·lar</Button>
            <Button type="submit" disabled={saving} className="bg-[#7B2D3F] hover:bg-[#6a2636] text-white">
              {saving ? "Desant..." : "Desar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}